import React, { createContext, useContext, useEffect, useState } from 'react';

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({
    // CIN-6784: merchant name (sanitized server-side). Null until the settings
    // fetch lands, and null forever if the backend does not return it.
    company_name: null,
    logo_url: null,
    primary_color: '#007bff',
    secondary_color: '#6c757d',
    font_family: 'Arial, sans-serif',
    font_url: null
  });
  const [variant, setVariant] = useState('v1');
  const [widgetConfig, setWidgetConfig] = useState({
    progress_indicator: 'line', // options: 'stepper', 'bar', 'line', 'none'
    show_search: false, // show search bar on services step
    allow_dropoffs: false // show dropoff checkbox on personal info step. DEPRECATED: use extra fields instead
  });

  // Apply dynamic styling based on branding
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', branding.primary_color);
    root.style.setProperty('--secondary-color', branding.secondary_color);
    root.style.setProperty('--font-family', branding.font_family);

    // Convert hex to RGB for alpha support
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };

    // Convert RGB to hex
    const rgbToHex = (r, g, b) => {
      return "#" + [r, g, b].map(x => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      }).join("");
    };

    // Apply config color overrides if provided
    const applyConfigColors = (config) => {
      if (config?.colors?.primaryButton) {
        if (config.colors.primaryButton.background) {
          root.style.setProperty('--primary-button-bg', config.colors.primaryButton.background);
        } else {
          root.style.setProperty('--primary-button-bg', 'var(--primary-color)');
        }

        if (config.colors.primaryButton.text) {
          root.style.setProperty('--primary-button-text', config.colors.primaryButton.text);
        } else {
          root.style.setProperty('--primary-button-text', 'var(--secondary-color-light)');
        }
      } else {
        // Fallback to defaults
        root.style.setProperty('--primary-button-bg', 'var(--primary-color)');
        root.style.setProperty('--primary-button-text', 'var(--secondary-color-light)');
      }

      if (config?.colors?.secondaryButton) {
        if (config.colors.secondaryButton.background) {
          root.style.setProperty('--secondary-button-bg', config.colors.secondaryButton.background);
        } else {
          root.style.setProperty('--secondary-button-bg', 'var(--secondary-color)');
        }

        if (config.colors.secondaryButton.text) {
          root.style.setProperty('--secondary-button-text', config.colors.secondaryButton.text);
        } else {
          root.style.setProperty('--secondary-button-text', 'var(--primary-color-light)');
        }
      } else {
        // Fallback to defaults
        root.style.setProperty('--secondary-button-bg', 'var(--secondary-color)');
        root.style.setProperty('--secondary-button-text', 'var(--primary-color-light)');
      }
    };

    const primaryRgb = hexToRgb(branding.primary_color);
    const secondaryRgb = hexToRgb(branding.secondary_color);

    if (primaryRgb && secondaryRgb) {
      root.style.setProperty('--primary-color-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
      root.style.setProperty('--secondary-color-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);

      // Calculate darker primary color for hover states (darken by 15%)
      const darkerPrimary = {
        r: Math.round(primaryRgb.r * 0.85),
        g: Math.round(primaryRgb.g * 0.85),
        b: Math.round(primaryRgb.b * 0.85)
      };

      root.style.setProperty('--primary-color-dark', rgbToHex(darkerPrimary.r, darkerPrimary.g, darkerPrimary.b));

      // Set default light colors (can be overridden by config)
      root.style.setProperty('--primary-color-light', branding.primary_color);
      root.style.setProperty('--secondary-color-light', branding.secondary_color);
    }

    // Apply config color overrides
    applyConfigColors(widgetConfig);

    // CIN-6784: Reserve with Google opens the action link as a top-level page, so
    // the document title is part of the landing page and must carry the merchant
    // name. Falls back to the static title shipped in public/index.html when the
    // backend does not return a company name.
    document.title = branding.company_name || 'Book an Appointment';

    // Add variant class to body for variant-specific styling
    document.body.className = document.body.className.replace(/widget-v\d+/g, '');
    document.body.classList.add(`widget-${variant}`);

    // Load custom font if font_url is provided
    if (branding.font_url && branding.font_url.trim()) {
      const existingLink = document.getElementById('custom-font-link');
      if (existingLink) {
        existingLink.remove();
      }

      const link = document.createElement('link');
      link.id = 'custom-font-link';
      link.rel = 'stylesheet';
      link.href = branding.font_url;
      document.head.appendChild(link);

      console.log('Loaded custom font:', branding.font_url);
    }
  }, [branding, variant, widgetConfig]);

  return (
    <BrandingContext.Provider value={{ 
      branding, 
      setBranding, 
      variant, 
      setVariant, 
      widgetConfig, 
      setWidgetConfig 
    }}>
      {children}
    </BrandingContext.Provider>
  );
};